import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'

import { useQuery } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import moment from 'moment'

import { getAccountId, getWhiteLabelPublisherId, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import { useGetFetchQuery } from '~/hooks/useGetFetchQuery'
import useQueryParams from '~/hooks/useQuery'
import { adTypesOptions } from '~/modules/retailMedia/constants/adTypesOptions'
import { CampaignChannel } from '~/modules/retailMedia/constants/campaignChannel'
import { useAdTypes } from '~/modules/retailMedia/hooks/useAdTypes'
import {
  getCampaignPermissionsService,
  getCampaignPermissionsServiceKey
} from '~/modules/retailMedia/services/configuration/campaign-permissions'

import { publishersWithAdditionalAssetsForVideo } from './constants/publishersWithAdditionalAssetsForVideo'

const CampaignFormContext = createContext<CampaignFormProviderData>(
  {} as CampaignFormProviderData
)

const CampaignFormProvider = ({ children }: React.PropsWithChildren) => {
  const formRef = useRef<FormHandles>(null)

  const [campaignPeriod, setCampaignPeriod] =
    useState<NameAndTimeIntervalOnChange>()

  const [selectedAudience, setSelectedAudience] =
    useState<AudienceData | null>()
  const [costType, setCostType] = useState<CampaignCostType>('cpmcpc')

  const [selectedSubpublishers, setSelectedSubpublishers] = useState<
    PublisherId[]
  >([])
  const [allSubpublishers, setAllSubpublishers] = useState<SubpublisherInfo[]>(
    []
  )

  const [screenGroups, setScreenGroups] = useState<ScreenGroup[]>([])

  const [linkedInsertionOrder, setLinkedInsertionOrder] =
    useState<InsertionOrder | null>(null)
  const [linkedInsertionOrderError, setLinkedInsertionOrderError] = useState<
    string | null
  >(null)

  // Offsite briefing channel (FR-015). Lifted out of the Channel component
  // so sections downstream of it — media size limit, Meta info panel,
  // review step — can read the selection reactively (see FR-004e: they
  // must derive from the channel, never from the permission).
  const [selectedChannel, setSelectedChannel] =
    useState<CampaignChannel | null>(null)

  // CampaignFormProviderData declares this setter with CampaignChannelValue
  // (see hooks/types.d.ts), the global mirror of the CampaignChannel enum,
  // rather than the enum itself. String enum members aren't structurally
  // assignable from bare string literals, so the cast below is required for
  // the exposed setter to satisfy that signature. Named distinctly from the
  // state setter above (exposed under the `setSelectedChannel` key on the
  // context value instead) so both keep the plain useState value/setter
  // naming pair.
  const setSelectedChannelForContext = useCallback(
    (channel: CampaignChannelValue | null) =>
      setSelectedChannel(channel as CampaignChannel | null),
    []
  )

  // Audience & segmentation gates (FR-017h/j) — lifted from AudienceTargeting
  // so ReviewStep can read whether Interests/imported-audience is currently
  // enabled for the Segmentation row's label (the underlying field values
  // persist across a gate being unmarked per FR-017j, so their mere
  // presence in the form snapshot isn't a reliable signal on its own).
  const [interestsEnabled, setInterestsEnabled] = useState(false)
  const [audienceImportEnabled, setAudienceImportEnabled] = useState(false)

  // CSV de audiência já enviado ao armazenamento (FR-052j). Desmarcar o gate
  // esconde a área sem descartar o arquivo (FR-017j) — quem descarta é o botão
  // de remover do próprio componente.
  const [audienceFile, setAudienceFile] = useState<AudienceFileLink | null>(
    null
  )

  const { i18n, t } = useTranslation()
  const { isWhiteLabel, isNetwork, superAdmin } = useAppInfo()

  // Handle query params
  const params = useQueryParams()
  const rmid = params.get('rmid') as PublisherId
  const audienceParam = params.get('audience')
  const audienceId = audienceParam
    ? (Number(audienceParam) as AudienceData['id'])
    : null
  const type = params.get('type') as CampaignAdType

  // `rmid` also identifies the advertiser when the logged-in account is a
  // publisher composing on their behalf (pages/campaigns/create reads the same
  // param as PublisherId | AdvertiserId) -- the provider survives that switch
  // (only the query param changes, no remount), so without this the CSV
  // uploaded for one advertiser would still be attached, and submitted, to the
  // next advertiser's campaign (found in review, 2026-09-25). Skips the first
  // render: that is the initial value being set, not a change to react to.
  const isFirstRmidRender = useRef(true)
  useEffect(() => {
    if (isFirstRmidRender.current) {
      isFirstRmidRender.current = false
      return
    }

    setAudienceFile(null)
  }, [rmid])

  // Handle publisher selection
  const isPublisherContextResolved = useMemo(
    () => isWhiteLabel || !!rmid,
    [isWhiteLabel, rmid]
  )
  const showPublisherFieldset = useMemo(
    () => !isPublisherContextResolved,
    [isPublisherContextResolved]
  )

  // Handle change type
  const handleType = useCallback(
    (type: CampaignAdType) => {
      // Channel/segmentation gates are tied to a single offsite briefing
      // session (FR-015/FR-017h/j): reset them here so a stale selection
      // from a previous type doesn't leak into the new one.
      setSelectedChannel(null)
      setInterestsEnabled(false)
      setAudienceImportEnabled(false)
      setAudienceFile(null)
      // The publisher/retailer selection is shared by every type, but each one
      // asks for it in its own panel; a leftover from the previous type would
      // read as already chosen (and as a destination retailer on offsite).
      setSelectedSubpublishers([])

      params.set('type', type)
      history.push({ search: params.toString() })
    },
    [params]
  )

  /**
   * Handle campaign permissions
   */
  // A conta entra na key pelo mesmo motivo de useAdTypes: o QueryClient
  // sobrevive à troca de conta. Sem isso, a trava leria a permissão da
  // conta anterior e apagaria o type da URL antes da resposta nova.
  const queryKeyCampaignPermissions = [
    getCampaignPermissionsServiceKey,
    getAccountId(),
    { publisher_id: rmid }
  ]

  const { data: campaignPermissions, status: statusCampaignPermissions } =
    useQuery({
      queryKey: queryKeyCampaignPermissions,
      queryFn: async () => {
        const response = await getCampaignPermissionsService({
          publisher_id: rmid
        })

        return response?.data
      }
    })

  // Handle network
  const publisherInfo = useGetFetchQuery<GetPublisherResponse>([
    'getAccountInfo',
    rmid
  ])

  const { data: resolvedAdTypes = [], isPending: adTypesPending } = useAdTypes()

  // Story is only valid when the publisher enabled it (flag + ad types list).
  // The card in CampaignType follows the same `storyIsEnabled`, so a direct
  // `?type=story` link falls into the invalid-type state. While the permission or
  // the list is loading the type is left as is, like restrictOffsiteFlow; an error
  // resolves to invalid. Only a successful read counts: a failed refetch keeps the
  // last data, whose flag would otherwise keep Story on.
  const storyIsEnabled =
    statusCampaignPermissions === 'success' &&
    resolvedAdTypes.includes('story') &&
    !!campaignPermissions?.allow_story_ads
  const storyIsResolved =
    statusCampaignPermissions !== 'pending' && !adTypesPending

  const adTypeIsValid = useMemo(() => {
    if (!adTypesOptions?.map(item => item?.value)?.includes(type)) return false

    if (type === 'story') return !storyIsResolved || storyIsEnabled

    return true
  }, [type, storyIsResolved, storyIsEnabled])

  const publisherIsNetwork = useMemo(
    () => publisherInfo?.is_network || isNetwork,
    [isNetwork, publisherInfo?.is_network]
  )

  const allowManageSubpublishers = useMemo(
    () => campaignPermissions?.allow_manage_subpublishers,
    [campaignPermissions?.allow_manage_subpublishers]
  )

  const allowAgeRestrictedCampaign = useMemo(
    () => campaignPermissions?.allow_age_restricted_campaign ?? false,
    [campaignPermissions?.allow_age_restricted_campaign]
  )

  /**
   * Handle campaign strategy
   */
  const strategyParam = useMemo(
    () => params.get('strategy') as CampaignStrategy,
    [params]
  )

  const allowObjectiveBasedCampaign = useMemo(
    () => campaignPermissions?.allow_simplified_campaign,
    [campaignPermissions?.allow_simplified_campaign]
  )

  const allowOffsiteBriefing = useMemo(
    () => campaignPermissions?.allow_offsite_briefing_campaign ?? false,
    [campaignPermissions?.allow_offsite_briefing_campaign]
  )

  // Só depois da permissão e da lista resolvida. Enquanto carrega, a opção
  // segue como está. Com banner_off_site na lista, a flag ligada e o modo
  // desligado, o tipo sai da URL para o formulário offsite desmontar.
  // replace evita deixar o endereço restrito no histórico: Voltar sairia
  // dele e este efeito empurraria a URL limpa de novo.
  const restrictOffsiteFlow =
    statusCampaignPermissions === 'success' &&
    !adTypesPending &&
    resolvedAdTypes.includes('banner_off_site') &&
    allowOffsiteBriefing &&
    !superAdmin

  useEffect(() => {
    if (!restrictOffsiteFlow) return

    setAudienceImportEnabled(false)
    setAudienceFile(null)

    if (type !== 'banner_off_site') return

    params.delete('type')
    history.replace({ search: params.toString() })
  }, [restrictOffsiteFlow, type, params])

  const [selectedStrategy, setSelectedStrategy] =
    useState<CampaignStrategy>(null)

  const [defaultStrategy, setDefaultStrategy] = useState<CampaignStrategy>(null)

  const handleStrategy = useCallback(
    (strategy: CampaignStrategy) => {
      if (
        !campaignPermissions?.allow_simplified_campaign ||
        !campaignPermissions?.strategy_types.includes(strategy)
      ) {
        return
      }

      setSelectedStrategy(strategy)
    },
    [
      campaignPermissions?.allow_simplified_campaign,
      campaignPermissions?.strategy_types
    ]
  )

  const strategyIsValid = useCallback(
    (strategy: CampaignStrategy) =>
      campaignPermissions?.strategy_types.includes(strategy),
    [campaignPermissions?.strategy_types]
  )

  useEffect(() => {
    if (strategyParam && strategyIsValid(strategyParam))
      handleStrategy(strategyParam)
  }, [handleStrategy, strategyIsValid, strategyParam])

  useEffect(() => {
    if (!allowObjectiveBasedCampaign) {
      setDefaultStrategy('standard')
      return
    }

    const strategyTypes = campaignPermissions?.strategy_types ?? []
    const defaultType = campaignPermissions?.default_strategy_type

    if (strategyTypes.length === 1) {
      setDefaultStrategy(strategyTypes[0])
      return
    }

    if (defaultType && strategyTypes.includes(defaultType)) {
      setDefaultStrategy(defaultType)
      return
    }

    setDefaultStrategy('standard')
  }, [
    allowObjectiveBasedCampaign,
    campaignPermissions,
    campaignPermissions?.default_strategy_type,
    campaignPermissions?.strategy_types
  ])

  const selectedStategyCampaign = useMemo(
    () => selectedStrategy || defaultStrategy,
    [defaultStrategy, selectedStrategy]
  )

  const availableStrategies = useMemo(() => {
    if (!allowObjectiveBasedCampaign || !campaignPermissions?.strategy_types)
      return ['standard'] as CampaignStrategy[]
    return campaignPermissions?.strategy_types
  }, [allowObjectiveBasedCampaign, campaignPermissions?.strategy_types])

  /**
   * Form is ready
   */

  const formIsReady = useMemo(
    () => !!campaignPermissions && statusCampaignPermissions !== 'pending',
    [campaignPermissions, statusCampaignPermissions]
  )

  /**
   * Debug mode
   * Availables keys:
   * - submit: print the request body and disable the form submission
   */
  const debugMode = useMemo(
    () => (superAdmin && params.get('debug')) || null,
    [params, superAdmin]
  )

  const debugSubmit = useCallback((body: Record<string, unknown>) => {
    console.log(
      `🚀 ~ DEBUG MODE:
        este modo imprime o payload da requisição
        e desabilita o envio do formulário.`
    )
    console.log('🚀 ~ request body:', body)
  }, [])

  const handleDebugSubmit = debugMode === 'submit' ? debugSubmit : undefined

  const toggleDebugMode = useCallback(() => {
    if (params.get('debug')) {
      params.delete('debug')
    } else {
      params.set('debug', 'submit')
    }

    history.push({ search: params.toString() })
  }, [params])

  const campaignType: CampaignType = useMemo(
    () => adTypesOptions?.find(item => item?.value === type)?.type,
    [type]
  )

  /**
   * Temporary
   */

  const additionalAssetsForVideoIsRequired = useMemo(
    () =>
      publishersWithAdditionalAssetsForVideo.includes(
        rmid || getWhiteLabelPublisherId()
      ),
    [rmid]
  )

  const defaultCampaignName = useMemo(() => {
    const adTypeLabel =
      adTypesOptions.find(item => item.value === type)?.label || type
    const today = moment().format(t('c:patterns.date'))

    return [adTypeLabel, today]
      .filter((part): part is string => !!part)
      .join(' - ')
  }, [i18n.resolvedLanguage, t, type])

  const contextValue = useMemo(
    () => ({
      rmid,
      formRef,

      campaignPermissions,
      formIsReady,
      selectedType: type,
      campaignPeriod,
      setCampaignPeriod,
      // Network
      isNetwork: publisherIsNetwork,
      allowManageSubpublishers,
      allowAgeRestrictedCampaign,
      allowOffsiteBriefing,
      restrictOffsiteFlow,
      superAdmin,
      selectedChannel,
      setSelectedChannel: setSelectedChannelForContext,
      interestsEnabled,
      audienceFile,
      setAudienceFile,
      setInterestsEnabled,
      audienceImportEnabled,
      setAudienceImportEnabled,
      // audience
      audienceId,
      selectedAudience,
      setSelectedAudience,
      adTypeIsValid,
      storyIsEnabled,
      campaignType,
      isPublisherContextResolved,
      showPublisherFieldset,
      // Strategy
      allowObjectiveBasedCampaign,
      selectedStategyCampaign,
      availableStrategies,
      // Subpublishers
      selectedSubpublishers,
      setSelectedSubpublishers,
      allSubpublishers,
      setAllSubpublishers,

      toggleDebugMode,
      handleDebugSubmit,

      handleType,
      costType,
      setCostType,

      // in store
      screenGroups,
      setScreenGroups,

      defaultCampaignName,

      linkedInsertionOrder,
      setLinkedInsertionOrder,
      linkedInsertionOrderError,
      setLinkedInsertionOrderError,

      // Temporary
      additionalAssetsForVideoIsRequired
    }),
    [
      rmid,
      formRef,
      campaignPermissions,
      formIsReady,
      type,
      campaignPeriod,
      setCampaignPeriod,
      publisherIsNetwork,
      allowManageSubpublishers,
      allowAgeRestrictedCampaign,
      allowOffsiteBriefing,
      restrictOffsiteFlow,
      superAdmin,
      selectedChannel,
      setSelectedChannelForContext,
      interestsEnabled,
      audienceFile,
      audienceImportEnabled,
      audienceId,
      selectedAudience,
      setSelectedAudience,
      adTypeIsValid,
      storyIsEnabled,
      campaignType,
      isPublisherContextResolved,
      showPublisherFieldset,
      allowObjectiveBasedCampaign,
      selectedStategyCampaign,
      availableStrategies,
      selectedSubpublishers,
      setSelectedSubpublishers,
      allSubpublishers,
      setAllSubpublishers,
      toggleDebugMode,
      handleDebugSubmit,
      handleType,
      costType,
      setCostType,
      screenGroups,
      setScreenGroups,
      defaultCampaignName,
      linkedInsertionOrder,
      setLinkedInsertionOrder,
      linkedInsertionOrderError,
      setLinkedInsertionOrderError,
      additionalAssetsForVideoIsRequired
    ]
  )

  return (
    <CampaignFormContext.Provider value={contextValue}>
      {children}
    </CampaignFormContext.Provider>
  )
}
function useCampaignForm(): CampaignFormProviderData {
  const context = useContext(CampaignFormContext)

  if (!context) {
    throw new Error(
      'useCampaignForm must be used within an CampaignFormProvider'
    )
  }

  return context
}

export { CampaignFormContext, CampaignFormProvider, useCampaignForm }
